/** @jsxImportSource @emotion/react */
import { Header, useSession } from '@quickbit/backoffice-core-frontend';
import {
  Icon,
  createStyles,
  Typography,
  useTheme,
  Input,
  Modal,
  Tooltip,
  UtilityButton,
} from '@quickbit/qb-design-system';
import { MouseEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAsyncFn } from 'react-use';
import { QBLoader } from 'components';
import { searchSkinningCustomers, approveSkinningCustomer } from '../api';
import { hasProofImages, SkinningCustomer } from '../types';

export const SkinningCustomerDetails = () => {
  const session = useSession();
  const navigate = useNavigate();
  const { spacing, palette } = useTheme();
  const styles = useStyles();
  const { userId: customerId } = useParams();
  const [customer, setCustomer] = useState<SkinningCustomer | null>(null);

  const hasEditPermission =
    session.authenticated && session.permissions.includes('edit_qb_users');

  const [hasImages, setHasImages] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [reasonForUnapproval, setReasonForUnapproval] = useState('');

  const canApproveOrUnapprove = customer?.isVerified || hasImages;

  const [approveUnapproveLoading, setApproveUnapproveLoading] = useState(false);

  const [{ loading: searchLoading }, search] = useAsyncFn(async () => {
    if (customerId) {
      const { customers } = await searchSkinningCustomers({
        id: customerId,
      });
      if (customers.length > 0) {
        const c = customers[0];
        setCustomer(c);
        setHasImages(hasProofImages(c));
      } else {
        setHasImages(false);
      }
    }
  }, [customerId]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, changeApprovalStatus] = useAsyncFn(async () => {
    if (!customer) return;

    setApproveUnapproveLoading(true);
    await approveSkinningCustomer(
      customer.id,
      !customer.isVerified,
      reasonForUnapproval
    );
    setApproveUnapproveLoading(false);
    void navigate(-1);
  }, [customer]);

  useEffect(() => {
    if (!customer) {
      void search();
      return;
    }
  }, [customer, search]);

  const handleViewCustomer = (e: MouseEvent<HTMLElement>, qbId: string) => {
    e.stopPropagation();
    void navigate(`/users/app/${qbId}`);
  };

  if (searchLoading) return <QBLoader />;

  const isVerified = customer?.isVerified ? true : false;

  return (
    <div>
      <Header>
        <div css={styles.headerContent}>
          <div css={styles.titleContainer}>
            <div
              css={{
                cursor: 'pointer',
                ':hover': {
                  textDecoration: 'underline',
                  textDecorationColor: palette.grey.x300,
                },
              }}
              onClick={() => navigate('/users/skinning')}
            >
              <Typography variant="subtitle" color={palette.grey.x300}>
                Skinning Customers
              </Typography>
            </div>
            <div css={styles.chevron}>
              <Icon name="chevron" size={spacing.xxs} />
            </div>
            <Typography variant="subtitle">
              {customer?.qbId ? (
                <a
                  href=""
                  onClick={(e) => handleViewCustomer(e, customer.qbId)}
                >
                  {customer.firstName} {customer.lastName}
                </a>
              ) : (
                `${customer?.firstName} ${customer?.lastName}`
              )}
            </Typography>
            {isVerified && (
              <div
                css={{
                  paddingLeft: '2em',
                }}
              >
                <Tooltip
                  title={`${customer?.firstName} ${customer?.lastName} is approved`}
                >
                  <Icon name="checkmark" size={spacing.s} />
                </Tooltip>
              </div>
            )}
          </div>
          <Tooltip
            title={
              customer?.isVerified
                ? 'Unapprove user'
                : 'Approve user (can be done once a customer has uploaded proof of id)'
            }
          >
            <UtilityButton
              text={customer?.isVerified ? 'Unapprove' : 'Approve'}
              variant="primary"
              loading={approveUnapproveLoading}
              disabled={
                !hasEditPermission ||
                !canApproveOrUnapprove ||
                approveUnapproveLoading
              }
              onClick={() => {
                setModalOpen(true);
              }}
            />
          </Tooltip>
        </div>
      </Header>
      <div css={styles.wrappingContainer}>
        <div css={[styles.container, styles.inputWrapper]}>
          <Input
            label="User ID"
            value={customer?.id || ''}
            onChange={() => ({})}
            disabled={true}
          />
          <Input label="Email" value={customer?.email || ''} disabled={true} />
          <Input
            label="Country"
            value={`${customer?.country}`}
            disabled={true}
          />
        </div>
      </div>
      <br />
      {!customer?.isVerified && !hasImages && (
        <p css={styles.helpText}>
          <em>
            This customer hasn&apos;t uploaded proof of id yet. Once they do,
            you will see it here, and they can then be approved.
          </em>
        </p>
      )}
      <div css={styles.proofContainer}>
        <div css={[styles.container, styles.proofWrapper]}>
          <h4>Proof of ID</h4>
          <div css={styles.idProofContainer}>
            {customer?.idProof.page1 && (
              <img src={customer.idProof.page1} css={styles.proofImage} />
            )}
            {customer?.idProof.page2 && (
              <img src={customer.idProof.page2} css={styles.proofImage} />
            )}
            {customer?.idProof.page3 && (
              <img src={customer.idProof.page3} css={styles.proofImage} />
            )}
          </div>
        </div>
        <div css={[styles.container, styles.proofWrapper]}>
          <h4>Proof of address</h4>
          <div css={styles.idProofContainer}>
            {customer?.addressProof.page1 && (
              <img src={customer.addressProof.page1} css={styles.proofImage} />
            )}
            {customer?.addressProof.page2 && (
              <img src={customer.addressProof.page2} css={styles.proofImage} />
            )}
            {customer?.addressProof.page3 && (
              <img src={customer.addressProof.page3} css={styles.proofImage} />
            )}
          </div>
        </div>
      </div>
      {modalOpen && (
        <Modal
          title={customer?.isVerified ? 'Unapprove' : 'Approve'}
          subtitle={`Confirm ${
            customer?.isVerified ? 'unapproval' : 'approval'
          } of ${customer?.firstName} ${customer?.lastName}`}
          secondaryButtonProps={{
            text: 'Cancel',
            onClick: () => setModalOpen(false),
          }}
          primaryButtonProps={{
            text: 'Confirm',
            onClick: () => {
              setModalOpen(false);
              void changeApprovalStatus();
            },
            disabled: !!customer?.isVerified && !reasonForUnapproval,
          }}
          closeModal={() => setModalOpen(false)}
        >
          {isVerified && (
            <div style={{ marginTop: '1em' }}>
              <Input
                label="Reason for unapproval"
                value={reasonForUnapproval}
                onChange={(e) => setReasonForUnapproval(e.target.value)}
              />
            </div>
          )}
          <br />
        </Modal>
      )}
    </div>
  );
};

const useStyles = createStyles(({ spacing, shape, palette }) => ({
  wrappingContainer: {
    width: '100%',
    display: 'flex',
    gap: spacing.xs,
  },
  container: {
    background: palette.white,
    borderRadius: shape.borderRadius,
  },
  headerContent: {
    display: 'grid',
    gap: spacing.xxs,
    gridTemplateColumns: '1fr auto auto',
  },
  titleContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  inputWrapper: {
    marginRight: 'auto',
    display: 'grid',
    gridAutoFlow: 'column',
    flexBasis: '100%',
    flex: 1,
    flexDirection: 'column',
    gap: spacing.xxs,
    width: 'calc(50%)',
    border: `1px solid ${palette.grey.x100}`,
  },
  proofContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: '100%',
  },
  idProofContainer: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    width: '100%',
  },
  proofImage: {
    maxWidth: '100%',
    height: 'auto',
    marginBottom: '1em',
  },
  proofWrapper: {
    padding: spacing.xxs,
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '100%',
    flex: 1,
    gap: spacing.xxs,
    border: `1px solid ${palette.grey.x100}`,
  },
  listItemBorder: {
    borderRadius: spacing.xxxs,
    border: `1px solid ${palette.grey.x200}`,
    ':hover': {
      borderColor: palette.grey.x600,
    },
  },
  listItemDisabled: {
    ':hover': {
      borderColor: 'inherit',
    },
  },
  chevron: {
    transform: 'rotate(180deg)',
    padding: `0 ${spacing.xxs}`,
    svg: { fill: 'none', stroke: palette.grey.x300 },
  },
  helpText: {
    marginLeft: spacing.xxxs,
  },
}));
